import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useUser } from "src/atoms/user";
import { useIsFeatureFlagEnabled } from "src/components/FeatureFlagProvider/store/hooks";
import {
  PogAgentErrorInfo,
  PogAgentFailure,
  describePogAgentError,
  isIntentClarificationError,
  isPogAgentEnabled,
  postPogAgentRun,
} from "./api";
import {
  FollowUpAction,
  combineApplyPages,
  createdPogIdFor,
  followUpFor,
  hasWrittenCurrentPog,
  previewPogFor,
} from "./followUp";
import {
  AgentApplyScope,
  AgentIntent,
  PlanHandover,
  PogAgentRunRequest,
  PogAgentRunResponse,
  PogAgentTarget,
} from "./types";

type ChatMessageBody =
  | { role: "user"; text: string }
  | {
      role: "assistant";
      response: PogAgentRunResponse;
      /** Saved-POG revision the request was made against, taken when it started. */
      pogRevision: number;
      /** Set when a batched apply stopped part-way; `response` holds the pages that finished. */
      failure?: PogAgentFailure;
    }
  /** The agent could not tell what the prompt asks for; the panel asks the merchandiser. */
  | { role: "clarify"; prompt: string }
  | { role: "error"; error: PogAgentErrorInfo };

export type ChatMessage = ChatMessageBody & { id: string };
const conversationCache = new Map<
  string,
  {
    messages: ChatMessage[];
    planHandover: PlanHandover | null;
    applicableMessageId: string | null;
    sourceHash?: string | null;
  }
>();

const cinemaPrompt = (prompt: string, context: string | undefined, messages: ChatMessage[]) => {
  const limit = 4000;
  const request = prompt.slice(0, limit);
  const remaining = limit - request.length;
  if (remaining <= 0) return request;
  const details = `\n\nReview context: ${context ?? "Current plan only"}\nPrevious conversation for reference: ${JSON.stringify(messages.slice(-6).flatMap((message) => (message.role === "user" ? [{ role: "user", text: message.text }] : message.role === "assistant" ? [{ role: "assistant", text: message.response.message }] : [])))}`;
  return request + details.slice(0, remaining);
};

/** How far a batched apply has got, for the spinner line while pages are still coming in. */
export interface ApplyProgress {
  done: number;
  total: number;
}

/** What a run hands back: the merged response, plus whatever cut it short, if anything. */
interface RunOutcome {
  response: PogAgentRunResponse;
  failure?: PogAgentFailure;
}

interface Args {
  previewOnly?: boolean;
  sourceHash?: string | null;
  context?: string;
  restrictToPog?: boolean;
  merchflowId: number;
  target: PogAgentTarget;
  /** Bumped by the page on every save of this POG; stamped on each turn when it starts. */
  savedPogRevision: number;
  /** Reload the planogram from OWA after a write. The agent does not touch the canvas. */
  onPogWritten: () => void;
  /** A save-as-new apply made a new store POG; the one on screen is unchanged. */
  onPogCreated?: (storePogId: number) => void;
}

export const usePogAgentChat = ({
  previewOnly = false,
  sourceHash,
  context,
  restrictToPog = false,
  merchflowId,
  target,
  savedPogRevision,
  onPogWritten,
  onPogCreated,
}: Args) => {
  const { user } = useUser();
  const chatKey = `${user?.merchantId ?? "unknown"}:${merchflowId}:${target.pogId}${previewOnly ? `:${sourceHash ?? "missing"}` : ""}`;
  const remembered = restrictToPog ? conversationCache.get(chatKey) : undefined;
  const [messages, setMessages] = useState<ChatMessage[]>(() => remembered?.messages ?? []);
  const [progress, setProgress] = useState<ApplyProgress | null>(null);
  /** What the pending run is, so the page can lock the canvas while a write is out. */
  const [runKind, setRunKind] = useState<"preview" | "apply" | null>(null);
  /**
   * planHandover of the most recent preview, and the turn whose card may offer
   * it. A new prompt replaces both the moment it is sent — not when it comes
   * back — so a prompt that fails or asks for clarification cannot leave the
   * previous turn's Apply button live on a plan that is no longer current. A
   * replay (apply, retry, resume) keeps them, so a failed replay can be retried.
   */
  const [planHandover, setPlanHandover] = useState<PlanHandover | null>(() =>
    remembered?.sourceHash === sourceHash ? (remembered?.planHandover ?? null) : null,
  );
  const [applicableMessageId, setApplicableMessageId] = useState<string | null>(() =>
    remembered?.sourceHash === sourceHash ? (remembered?.applicableMessageId ?? null) : null,
  );
  const nextId = useRef(remembered?.messages.length ?? 0);
  useEffect(() => {
    if (!restrictToPog) return;
    conversationCache.set(chatKey, { messages, planHandover, applicableMessageId, sourceHash });
    if (conversationCache.size > 30) {
      const [oldest] = [...conversationCache.keys()];
      if (oldest) conversationCache.delete(oldest);
    }
  }, [chatKey, messages, planHandover, applicableMessageId, sourceHash, restrictToPog]);

  /**
   * A run that finishes after this hook was unmounted, or after it was pointed
   * at another POG, must not touch the thread or call back into the page: the
   * page may by then be showing a different planogram, and a reload or a
   * navigation meant for the old one would land on the new one. Each run keeps
   * the generation it started under and checks it after every await.
   *
   * Closing the panel is not one of those events: the page keeps this hook
   * mounted while the panel is hidden, so a write that lands with the panel
   * closed still refreshes the planogram the merchandiser is looking at.
   */
  const generation = useRef(0);
  useEffect(() => {
    generation.current += 1;
    return () => {
      generation.current += 1;
    };
  }, [merchflowId, target.pogId, target.storeCode, sourceHash]);

  const append = (message: ChatMessageBody): string => {
    // The id is taken here, not inside the updater: React may run an updater
    // more than once, and a ref read inside it would hand out duplicate keys.
    nextId.current += 1;
    const id = String(nextId.current);
    setMessages((previous) => [...previous, { ...message, id }]);

    return id;
  };

  /**
   * One agent turn, following `applyBatch` pages until the agent says it is
   * done. Every page is asked with the same planHandover and the offset the
   * previous page handed back; the pages are then merged into one response so
   * the thread shows a single result for the whole apply.
   *
   * A page that fails after earlier pages wrote does not throw the writes
   * away: the outcome carries what finished, with `applyBatch.nextOffset` set
   * to the page that did not, so the follow-up resumes there instead of
   * replaying from the start over stores already done. A page whose
   * `nextOffset` does not advance is treated the same way — its rows are kept,
   * the stall is reported, and no resume is offered, since asking for the same
   * offset again would only stall again.
   */
  const runPaged = async (payload: PogAgentRunRequest): Promise<RunOutcome> => {
    if (
      previewOnly &&
      (payload.mode !== "preview" || payload.scope !== "pog" || payload.planHandover)
    )
      throw new Error("This Cinema session cannot apply an agent plan.");
    if (previewOnly) {
      const merchantId = user?.merchantId;
      if (
        !sourceHash ||
        typeof merchantId !== "number" ||
        !Number.isSafeInteger(merchantId) ||
        merchantId <= 0 ||
        !Number.isSafeInteger(payload.merchflowId) ||
        payload.merchflowId <= 0 ||
        payload.targets.length !== 1
      )
        throw new Error("Cinema preview needs one saved plan.");
    }
    const pages: PogAgentRunResponse[] = [];
    let applyOffset = payload.options?.applyOffset ?? 0;

    for (;;) {
      let page: PogAgentRunResponse;
      try {
        page = await postPogAgentRun(
          applyOffset === 0
            ? payload
            : { ...payload, options: { ...payload.options, applyOffset } },
        );
      } catch (error) {
        if (!pages.length) throw error;
        return {
          response: combineApplyPages(pages, applyOffset),
          failure: describePogAgentError(error),
        };
      }
      pages.push(page);

      if (previewOnly && (page.mode !== "preview" || page.applyBatch))
        throw new Error("The agent did not return a single-plan preview.");

      const batch = page.applyBatch;
      if (!batch || batch.nextOffset === null) break;
      if (batch.nextOffset <= applyOffset) {
        return { response: combineApplyPages(pages), failure: { kind: "batchStalled" } };
      }

      setProgress({ done: batch.offset + batch.count, total: batch.total });
      applyOffset = batch.nextOffset;
    }

    return { response: pages.length === 1 ? pages[0] : combineApplyPages(pages) };
  };

  // The run is a mutation: one request (or one page chain) that writes through
  // OWA. Its pending flag is the thread's spinner. Nothing in React Query
  // holds this POG — the page keeps it in its own atom — so a successful write
  // is handed back through the callbacks rather than an invalidation.
  const mutation = useMutation({ mutationFn: runPaged });

  const run = async (args: {
    prompt: string;
    userText: string;
    /** Forced intent. Left out, the agent classifies the prompt itself. */
    intent?: AgentIntent;
    planHandover?: PlanHandover;
    scope?: AgentApplyScope;
    saveAsNew?: boolean;
    applyOffset?: number;
  }) => {
    if (mutation.isPending) return;

    const startedUnder = generation.current;
    // Taken before the request goes out: a save that lands while the agent is
    // still working makes this preview stale, and stamping it on arrival would
    // hide that.
    const pogRevision = savedPogRevision;

    append({ role: "user", text: args.userText });
    setProgress(null);
    setRunKind(args.planHandover ? "apply" : "preview");

    // A new prompt supersedes the stored plan before the agent has even
    // answered; whatever the old card offered is no longer what would run.
    if (!args.planHandover) {
      setPlanHandover(null);
      setApplicableMessageId(null);
    }

    // A replayed plan is always an edit; otherwise only a forced intent is sent.
    const intent = args.planHandover ? "postprocess" : args.intent;

    try {
      const outcome = await mutation.mutateAsync({
        prompt:
          args.prompt && restrictToPog
            ? previewOnly
              ? cinemaPrompt(args.prompt, context, messages)
              : `${args.prompt}\n\nReview context: ${context ?? "Current plan only"}\nPrevious conversation for reference: ${JSON.stringify(messages.slice(-6).flatMap((message) => (message.role === "user" ? [{ role: "user", text: message.text }] : message.role === "assistant" ? [{ role: "assistant", text: message.response.message }] : [])))}`
            : args.prompt,
        merchflowId,
        targets: [target],
        // Same as the agent's debug UI: without it the agent can only use its
        // catalog skills and refuses anything they do not cover.
        options: {
          allowGeneratedCode: !previewOnly,
          ...(args.applyOffset ? { applyOffset: args.applyOffset } : {}),
        },
        ...(args.planHandover ? { planHandover: args.planHandover, mode: "apply" as const } : {}),
        ...(args.scope ? { scope: args.scope } : {}),
        ...(intent ? { intent } : {}),
        // Left out rather than sent as false: in-place is the agent's default
        // and the preferred way to save, to keep versions from piling up.
        ...(args.saveAsNew ? { saveAsNew: true } : {}),
        ...(restrictToPog || previewOnly
          ? {
              scope: "pog" as const,
              mode: args.planHandover ? ("apply" as const) : ("preview" as const),
            }
          : {}),
      });
      if (startedUnder !== generation.current) return;

      const { response, failure } = outcome;
      const id = append({ role: "assistant", response, pogRevision, failure });

      // Only a turn that can still be applied is worth keeping; anything else
      // clears the stored plan so a stale handover is never replayed.
      const isApplicable = previewOnly ? !!previewPogFor(response) : followUpFor(response) !== null;
      setPlanHandover(!previewOnly && isApplicable ? response.planHandover : null);
      setApplicableMessageId(isApplicable ? id : null);

      const createdPogId = previewOnly ? null : createdPogIdFor(response);
      if (createdPogId !== null) {
        onPogCreated?.(createdPogId);
      } else if (!previewOnly && hasWrittenCurrentPog(response, target.pogId)) {
        onPogWritten();
      }
    } catch (error) {
      if (startedUnder !== generation.current) return;

      if (args.prompt && !intent && isIntentClarificationError(error)) {
        // Not knowing whether a prompt is an edit or a question is the agent's
        // way of asking; put the choice in the thread rather than an error.
        append({ role: "clarify", prompt: args.prompt });
      } else {
        append({ role: "error", error: describePogAgentError(error) });
      }
    } finally {
      if (startedUnder === generation.current) {
        setProgress(null);
        setRunKind(null);
      }
    }
  };

  /**
   * A prompt on its own is always a preview: no planHandover is sent. The
   * intent is only passed when the merchandiser picked one after the agent
   * asked; the user bubble stays the prompt they typed.
   */
  const sendPrompt = (prompt: string, intent?: AgentIntent) =>
    run({ prompt, userText: prompt, intent });

  /** Apply replays the stored plan, so the agent never calls the LLM again. */
  const applyStoredPlan = (action: FollowUpAction, label: string) => {
    if (previewOnly) return;
    if (restrictToPog && (action.scope !== "pog" || action.saveAsNew)) return;
    if (!planHandover) return;

    return run({
      prompt: "",
      userText: label,
      planHandover,
      scope: action.scope,
      saveAsNew: action.saveAsNew,
      applyOffset: action.applyOffset,
    });
  };

  /** Put a failure that happened on our side, not the agent's, in the thread. */
  const reportError = (error: PogAgentErrorInfo) => append({ role: "error", error });

  return {
    messages,
    isRunning: mutation.isPending,
    runKind,
    progress,
    planHandover,
    /** The turn whose card may offer the stored plan; null once a newer prompt went out. */
    applicableMessageId,
    sendPrompt,
    applyStoredPlan,
    reportError,
  };
};

/** The deployment must point at an agent and the environment's `pog_agent` flag must be on. */
export const useIsPogAgentEnabled = () =>
  useIsFeatureFlagEnabled("pog_agent") && isPogAgentEnabled();
